import React, { useLayoutEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from "@mui/icons-material";
import StyleVariables from "../../assets/styles/Variable.module.scss";

type Props = {
  id: string;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  selectedNames?: string[];
  children: React.ReactNode;
  onEntered?: () => void;
};

const SouvenirAccordion = ({
  id,
  expanded,
  onChange,
  selectedNames = [],
  children,
  onEntered,
}: Props) => {
  const summaryRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useLayoutEffect(() => {
    const container = summaryRef.current;
    const measurement = measurementRef.current;

    if (!container || !measurement || !selectedNames.length) return;

    let active = true;

    const updateVisibleCount = () => {
      if (!active) return;

      const availableWidth = container.clientWidth;

      if (!availableWidth) return;

      for (let count = selectedNames.length; count >= 1; count -= 1) {
        const remainingCount = selectedNames.length - count;
        const suffix = remainingCount
          ? ` และอีก ${remainingCount} รายการ`
          : "";

        measurement.textContent =
          `(เลือกรับ ${selectedNames.slice(0, count).join(", ")}` + suffix + ")";

        if (measurement.getBoundingClientRect().width <= availableWidth) {
          setVisibleCount(count);
          return;
        }
      }

      setVisibleCount(0);
    };

    updateVisibleCount();

    const resizeObserver = new ResizeObserver(updateVisibleCount);
    resizeObserver.observe(container);

    void document.fonts.ready.then(updateVisibleCount);

    return () => {
      active = false;
      resizeObserver.disconnect();
    };
  }, [selectedNames]);

    const count = Math.min(visibleCount, selectedNames.length);
    const remainingCount = count > 0 ? selectedNames.length - count : 0;
    const selectedText =
      count > 0
        ? `(เลือก ${selectedNames.slice(0, count).join(", ")}`
        : `(${selectedNames.length} รายการ`;
    const fullSelectedText = `(เลือก ${selectedNames.join(", ")})`;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, nextExpanded) => onChange(nextExpanded)}
      TransitionProps={{ onEntered }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        aria-controls={`${id}-content`}
        id={`${id}-header`}
        sx={{
          "& .MuiAccordionSummary-content": {
            minWidth: 0,
          },
        }}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          gap={{ xs: 1, sm: 2 }}
          minWidth={0}
          width={"100%"}
          paddingRight={1}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            color={StyleVariables["color-header"]}
            sx={{ flexShrink: 0 }}
          >
            ของแถม
          </Typography>

          {selectedNames.length > 0 && (
            <Box
              ref={summaryRef}
              minWidth={0}
              flex={1}
              position={"relative"}
            >
              <Tooltip title={fullSelectedText} describeChild>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  minWidth={0}
                  aria-label={fullSelectedText}
                >
                  <Typography
                    variant="body2"
                    color={StyleVariables["color-black"]}
                    noWrap
                    sx={{ minWidth: 0 }}
                  >
                    {selectedText}
                  </Typography>

                  <Typography
                    variant="body2"
                    color={StyleVariables["color-black"]}
                    sx={{
                      flexShrink: 0,
                      whiteSpace: "pre",
                    }}
                  >
                    {remainingCount > 0
                      ? ` และอีก ${remainingCount} รายการ)`
                      : ")"}
                  </Typography>
                </Box>
              </Tooltip>

              <Typography
                ref={measurementRef}
                component="span"
                variant="body2"
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  visibility: "hidden",
                  pointerEvents: "none",
                  whiteSpace: "pre",
                  width: "max-content",
                }}
              />
            </Box>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails id={`${id}-content`}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default SouvenirAccordion;